import { ContextStore, ExperimentStore } from 'bernie-plugin-mobx';
import { inject } from 'mobx-react';
import * as React from 'react';

import { useLocalization } from '@shared-ui/localization-context';

import { getShoppingContextInput } from 'src/common/stores/adapters';
import { MultiItemStore } from 'src/common/stores/multi-item-store';
import { SearchResultsStore } from 'src/common/stores/search-results-store';

import { PackageType } from 'models/multi-item/multi-item-types';

import { UitkSpacing } from 'uitk-react-spacing';
import { UitkText } from 'uitk-react-text';

import { SearchForm } from 'components/common/search-form';

interface ChangeSearchFormProps {
  searchResults?: SearchResultsStore;
  multiItem?: MultiItemStore;
  context?: ContextStore;
  experiment?: ExperimentStore;
  afterSubmit?: ({ packageType, isMobile }) => void;
  isHidden?: boolean;
}

export const ChangeSearchForm: React.FunctionComponent<ChangeSearchFormProps> = inject(
  'searchResults',
  'multiItem',
  'context',
  'experiment'
)(({ searchResults, multiItem, context, experiment, afterSubmit, isHidden = false }) => {
  const onUpdateResults = () => {
    if (searchResults) {
      searchResults.clearDefaultRequestFields();
      searchResults.shouldFetch = true;
    }
  };

  const { formatText } = useLocalization();
  const sortAndFilter = searchResults && searchResults.sortAndFilter;
  const packageType = multiItem?.getPackageType();
  const shouldShowMultiItemWizardFields = multiItem?.isPackageShoppingPath();

  const isMobile = context && context.deviceInformation && context.deviceInformation.mobile;
  const displayFields: string[] = React.useMemo(() => getDisplayFields(packageType), [packageType]);

  const ignoreFields = React.useMemo(() => ['startDate', 'endDate', 'partialStayStartDate', 'partialStayEndDate'], []);
  const wrapperClasses = React.useMemo(
    () =>
      Object.keys(displayFields).map(key => ({
        [key]: 'uitk-cell all-y-gutter-two m-cell-1-1 l-cell-1-3 xl-cell-1-3',
      })),
    [displayFields]
  );

  const searchFormProps = {
    className: 'uitk-grid l-x-gutter-three all-x-gutter-two all-y-gutter-two',
    model: searchResults && searchResults.searchCriteria,
    sortAndFilterOptions: sortAndFilter && sortAndFilter.options,
    sortAndFilterDefaults: sortAndFilter && sortAndFilter.defaults,
    provideDefaultSubmitButton: true,
    autoSubmit: !packageType,
    fetchDatePickerRates: () => {
      const multiItemContext = multiItem?.multiItemContext;
      const shoppingContext = getShoppingContextInput(multiItemContext);

      return (
        (context && experiment && shoppingContext && searchResults?.fetchRateCalendar(context, experiment, shoppingContext)) || undefined
      );
    },
    isHidden: false,
    shouldShowMultiItemWizardFields,
    packageType,
    isMobile,
    afterSubmit,
    ignoreFields,
    displayFields,
    onUpdateResults,
    wrapperClasses,
  };

  return (
    <UitkSpacing margin={{ block: 'two' }} data-stid="section-hotel-search" className={isHidden ? 'is-hidden' : ''}>
      <div>
        {multiItem?.isTouchedCart && !multiItem?.isPackageShoppingPath() && (
          <UitkSpacing margin={{ blockend: 'two' }}>
            <UitkText size={400} className="pwa-theme--grey-700" weight="bold">
              {formatText('hotels.searchResults.multiProperty.searchCriteria.header')}
            </UitkText>
          </UitkSpacing>
        )}
        <h2 className="is-visually-hidden">{formatText('hotels.infosite.availabilityWizard.changeSearch.a11yLabel')}</h2>
        <SearchForm {...searchFormProps} />
      </div>
    </UitkSpacing>
  );
});

export const getDisplayFields = (packageType?: string) =>
  packageType && packageType !== PackageType.HC
    ? ['origin', 'destination', 'rooms', 'cabinClass', 'dateRange', 'directFlights', 'partialStayCheckbox', 'partialStayDateRange']
    : ['destination', 'dateRange', 'rooms', 'pickUpTime', 'dropOffTime'];
